/**
 * What a desktop e2e run's Flagsmith flags resolved to, readable from outside
 * the app.
 *
 * The native lane cannot read app state, but its build carries the dev bridge,
 * whose `POST /eval` runs a function body in the webview. So in a desktop e2e
 * build the override provider publishes `window.__HEIDI_E2E_FLAGS__`, and the
 * lane saves its result after every test (`e2e/desktop-native/lib/flag-snapshot.ts`).
 *
 * The snapshot carries both sides of the freeze: `resolved` is what the app
 * used, `live` is what Flagsmith served the test account. A key whose two
 * values differ is one the native lane sees differently from staging, which
 * is the list to pin from when a spec turns out to depend on staging.
 */

/** A flag as Flagsmith serves it. */
export type LiveFlag = { enabled: boolean; value?: unknown };
export type FlagEvaluationType = 'boolean' | 'string' | 'number' | 'object';
export type ResolvedFlag = Partial<Record<FlagEvaluationType, unknown>>;

export type E2eFlagSnapshot = {
  /** Whether live values were ignored (`isFlagsmithFrozen`). */
  frozen: boolean;
  /** The last value each flag resolved to for every evaluation type used. */
  resolved: Record<string, ResolvedFlag>;
  /** Flagsmith's values for this identity, by key; empty before the first fetch. */
  live: Record<string, LiveFlag>;
};

declare global {
  interface Window {
    __HEIDI_E2E_FLAGS__?: (reset?: boolean) => E2eFlagSnapshot;
  }
}

const resolvedFlags = new Map<string, ResolvedFlag>();

export const recordFlagsmithResolution = (
  flagKey: string,
  evaluationType: FlagEvaluationType,
  value: unknown
): void => {
  resolvedFlags.set(flagKey, {
    ...resolvedFlags.get(flagKey),
    [evaluationType]: value,
  });
};

export const installE2eFlagSnapshot = (
  options: { frozen: boolean; readLive: () => Record<string, LiveFlag> | undefined },
  win: Pick<Window, '__HEIDI_E2E_FLAGS__'> | undefined = typeof window === 'undefined'
    ? undefined
    : window
): void => {
  if (!win) return;
  win.__HEIDI_E2E_FLAGS__ = (reset = false) => {
    const snapshot = {
      frozen: options.frozen,
      resolved: Object.fromEntries(resolvedFlags),
      live: options.readLive() ?? {},
    };
    if (reset) resolvedFlags.clear();
    return snapshot;
  };
};

/** Test-only: forget recorded resolutions between cases. */
export const resetFlagsmithResolutions = (): void => {
  resolvedFlags.clear();
};
